/**
 * Main theme configuration for CoOrdio Cortex.
 * CoOrdio Design System with Cortex accent and IBM Plex Sans.
 */

// MUI Imports
import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

// Type Imports
import type { Settings } from '../store/settingsStore';
import type { SystemMode, Skin } from '../@core/types';

// Theme Options Imports
import overrides from './overrides';
import colorSchemes from './colorSchemes';
import spacing from './spacing';
import shadows from './shadows';
import customShadows from './customShadows';
import typography from './typography';

/**
 * Theme configuration function
 * @param settings - Settings object from store
 * @param mode - System mode (light or dark)
 * @param direction - Text direction (ltr or rtl)
 * @returns MUI Theme object
 */
const theme = (settings: Settings, mode: SystemMode, direction: Theme['direction'] = 'ltr'): Theme => {
  const coreTheme = {
    direction,
    components: overrides(settings.skin as Skin),
    colorSchemes: colorSchemes(settings.skin as Skin),
    cssVariables: {
      colorSchemeSelector: 'data-theme',
    },
    ...spacing,
    shape: {
      borderRadius: 12,
      customBorderRadius: {
        xs: 4,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 20,
      },
    },
    shadows: shadows(mode),
    typography: typography('"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'),
    customShadows: customShadows(mode),
    mainColorChannels: {
      light: '15 23 42',
      dark: '226 232 240',
      lightShadow: '15 23 42',
      darkShadow: '0 0 0',
    },
  };
  
  return createTheme(coreTheme) as Theme;
};

export default theme;
